import React, { useEffect, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../app/store';
import { fetchDashboardStatsAction } from '../requisitions/requisitionSlice'; // Assuming slice is in requisitions
import { formatCurrency } from '../../utils/formatUtils';
import apiClient from '../../services/api';

const DashboardPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const { dashboardStats, isLoadingDashboardStats, dashboardError } = useSelector(
    (state: RootState) => state.requisitions // Assuming dashboard stats are in requisitions slice
  );
  const { user } = useSelector((state: RootState) => state.auth);

  // Check if user is admin
  const isAdmin = user && (user as any).is_admin;

  // Helper to handle undefined/null values for currency formatting
  const formatCurrencyWithFallback = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return formatCurrency(value);
  };
  const [selectedAreaCoach, setSelectedAreaCoach] = useState<{ id: number; name: string } | null>(null);
  const [selectedRegionalCoach, setSelectedRegionalCoach] = useState<{ id: number; name: string } | null>(null);
  const [isAreaCoachesOpen, setIsAreaCoachesOpen] = useState(true);
  const [isRegionalCoachesOpen, setIsRegionalCoachesOpen] = useState(true);

  // Jobcard data state
  const [jcLoading, setJcLoading] = useState(false);
  const [jcError, setJcError] = useState<string | null>(null);
  const [jcSummary, setJcSummary] = useState<{ total: number; open: number; closed: number } | null>(null);
  const [jcRegionalCoaches, setJcRegionalCoaches] = useState<Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }>>([]);
  const [jcAreaCoaches, setJcAreaCoaches] = useState<Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }>>([]);
  const [jcStores, setJcStores] = useState<Array<{ id: number; name: string; storenumber?: string; open: number; closed: number; total: number; areaCoachId?: number; areaCoachName?: string }>>([]);
  const [storeSortKey, setStoreSortKey] = useState<'name' | 'open' | 'closed' | 'total' | 'pct'>('pct');
  const [storeSortDir, setStoreSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchJson = async (path: string, params?: Record<string, any>) => {
    try {
      const res = await apiClient.get(path, { params });
      console.debug('API response', { path, data: res.data });
      return res.data;
    } catch (e: any) {
      console.error('API error', { path, error: e?.response?.data || e?.message });
      throw e;
    }
  };

  // Initial load of summary and area coaches, refetch when impersonation changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setJcLoading(true);
        setJcError(null);
        const promises: Promise<any>[] = [
          fetchJson('/approver/jobcards/summary'),
        ];
        if (isAdmin) {
          promises.push(fetchJson('/approver/jobcards/regional-coaches'));
          promises.push(fetchJson('/approver/jobcards/area-coaches'));
        } else {
          promises.push(fetchJson('/approver/jobcards/area-coaches'));
        }
        const [summaryData, maybeRegional, acData] = isAdmin ? await Promise.all(promises) : await Promise.all([...promises, null as any]);
        if (!mounted) return;
        console.debug('Jobcards summary', summaryData);
        if (isAdmin) console.debug('Jobcards regionalCoaches', maybeRegional);
        console.debug('Jobcards areaCoaches', isAdmin ? acData : maybeRegional);
        setJcSummary(summaryData?.summary ?? null);
        if (isAdmin) {
          setJcRegionalCoaches(maybeRegional?.regionalCoaches ?? []);
          setJcAreaCoaches(acData?.areaCoaches ?? []);
        } else {
          setJcAreaCoaches(maybeRegional?.areaCoaches ?? []);
        }
      } catch (e: any) {
        if (!mounted) return;
        setJcError(e?.message || 'Failed to load job cards');
      } finally {
        if (mounted) setJcLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAdmin]); // Auto-load on component mount and when admin status changes

  // When admin selects a Regional Coach, refetch Area Coaches filtered by regionalId
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!isAdmin) return;
      try {
        setJcLoading(true);
        setJcError(null);
        const params = selectedRegionalCoach?.id ? { regionalId: selectedRegionalCoach.id } : undefined;
        const acData = await fetchJson('/approver/jobcards/area-coaches', params);
        if (!mounted) return;
        setJcAreaCoaches(acData?.areaCoaches ?? []);
      } catch (e: any) {
        if (!mounted) return;
        setJcError(e?.message || 'Failed to load area coaches');
      } finally {
        if (mounted) setJcLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAdmin, selectedRegionalCoach?.id]);

  // Load stores (filtered by selected AC if present), refetch when impersonation changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setJcLoading(true);
        setJcError(null);
        const params: any = {};
        // If area coach is selected, only filter by area coach (it already implies the regional)
        // Otherwise, filter by regional coach if selected
        if (selectedAreaCoach?.id) {
          params.areaCoachId = selectedAreaCoach.id;
        } else if (selectedRegionalCoach?.id) {
          params.regionalId = selectedRegionalCoach.id;
        }
        const data = await fetchJson('/approver/jobcards/stores', Object.keys(params).length ? params : undefined);
        console.debug('Jobcards stores', data);
        if (!mounted) return;
        setJcStores(data?.stores ?? []);
      } catch (e: any) {
        if (!mounted) return;
        setJcError(e?.message || 'Failed to load stores');
      } finally {
        if (mounted) setJcLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedAreaCoach, selectedRegionalCoach]); // Only trigger when impersonation status actually changes

  // Dynamic labels and descriptions based on user role
  const getCardContent = () => {
    if (isAdmin) {
      return {
        pendingTitle: "Pending Approvals",
        pendingDescription: "All requisitions awaiting any approver's action.",
        recentTitle: "Recently Approved",
        recentDescription: "All requisitions approved by any approver this week.",
        valueDescription: "Total value of all pending submitted requisitions across all stores."
      };
    } else {
      return {
        pendingTitle: "Pending My Approval",
        pendingDescription: "Requisitions awaiting your action.",
        recentTitle: "Recently Approved",
        recentDescription: "Requisitions approved by you this week.",
        valueDescription: "Total value of pending submitted requisitions from your assigned stores."
      };
    }
  };

  const cardContent = getCardContent();

  const InfoCard = ({
    label,
    value,
    gradient,
    icon,
  }: {
    label: string;
    value: ReactNode;
    gradient: string; // tailwind class e.g., "from-indigo-500 to-purple-600"
    icon?: ReactNode;
  }) => (
    <div className={`shadow-lg rounded-xl p-4 sm:p-6 text-white bg-gradient-to-br ${gradient} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}>
      <div className="flex flex-col">
        <div className="flex items-center gap-2 text-white/70 mb-1">
          {icon}
          <span className="text-sm sm:text-base">{label}</span>
        </div>
        <div className="font-bold text-2xl sm:text-4xl leading-tight min-h-[2rem] sm:min-h-[2.5rem] flex items-center">{value}</div>
      </div>
    </div>
  );

  const LoadingSpinner = ({
    size = 'md',
    label,
    variant = 'light',
  }: {
    size?: 'sm' | 'md' | 'lg';
    label?: string;
    variant?: 'light' | 'dark';
  }) => {
    const dimension = size === 'sm' ? 16 : size === 'lg' ? 36 : 24;
    const ringBase = variant === 'light' ? 'border-white/20 border-t-white' : 'border-gray-300 border-t-blue-500';
    const textClass = variant === 'light' ? 'text-white/80' : 'text-gray-600';
    return (
      <div className="flex items-center gap-2">
        <span
          className={`inline-block rounded-full ${ringBase} animate-spin`}
          style={{ width: dimension, height: dimension, borderWidth: Math.max(2, Math.floor(dimension / 8)) }}
        />
        {label && <span className={`text-sm ${textClass}`}>{label}</span>}
      </div>
    );
  };

  const getCompletionPct = (closed: number, total: number) => {
    if (!total || total <= 0) return 0;
    return Math.round((closed / total) * 100);
  };

  const exportStoresToCsv = (rows: Array<{ id: number; name: string; storenumber?: string; open: number; closed: number; total: number }>) => {
    const headers = ['Store', 'Open', 'Closed', 'Total', 'Completion %'];
    const lines = rows.map(r => {
      const total = r.open + r.closed;
      const pct = getCompletionPct(r.closed, total);
      const storeLabel = `${r.name}${r.storenumber ? ` - ${r.storenumber}` : ''}`.replaceAll(',', ' ');
      return [storeLabel, r.open, r.closed, total, `${pct}%`].join(',');
    });
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const rcSuffix = selectedRegionalCoach?.name ? `_${selectedRegionalCoach.name.replace(/\s+/g, '_')}` : '';
    const acSuffix = selectedAreaCoach?.name ? `_${selectedAreaCoach.name.replace(/\s+/g, '_')}` : '';
    link.download = `jobcards_stores${rcSuffix}${acSuffix}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    // Fetch dashboard stats on component mount
    dispatch(fetchDashboardStatsAction());
  }, [dispatch]);

  return (
    <div className="p-4">

      <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">
        Requisitions {isAdmin && <span className="text-sm font-normal text-blue-600 dark:text-blue-400">(Admin View)</span>}
      </h1>

      {/* Global Error Banner - shown if there's an error and not currently loading */}
      {dashboardError && !isLoadingDashboardStats && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md shadow text-center">
          Error loading dashboard statistics: {typeof dashboardError === 'string' ? dashboardError : 'An unknown error occurred.'}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Card 1: Pending Approval */}
        <InfoCard
          label={cardContent.pendingTitle}
          gradient="from-indigo-500 to-purple-600"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
            </svg>
          }
          value={
            isLoadingDashboardStats ? (
              <LoadingSpinner size="sm" variant="light" label="Loading..." />
            ) : dashboardError ? (
              <span className="text-white/80 text-base">Error</span>
            ) : (
              dashboardStats?.pendingMyApprovalCount ?? 'N/A'
            )
          }
        />

        {/* Card 2: Recently Approved */}
        <InfoCard
          label={cardContent.recentTitle}
          gradient="from-emerald-600 to-teal-400"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          }
          value={
            isLoadingDashboardStats ? (
              <LoadingSpinner size="sm" variant="light" label="Loading..." />
            ) : dashboardError ? (
              <span className="text-white/80 text-base">Error</span>
            ) : (
              dashboardStats?.recentlyApprovedCount ?? 'N/A'
            )
          }
        />

        {/* Card 3: Total Value (All Pending Submitted) */}
        <InfoCard
          label="Total Value (Pending Submitted)"
          gradient="from-amber-500 to-yellow-400"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-2.21 0-4 1.343-4 3s1.79 3 4 3 4 1.343 4 3-1.79 3-4 3m0-12V4m0 16v-2" />
            </svg>
          }
          value={
            isLoadingDashboardStats ? (
              <LoadingSpinner size="sm" variant="light" label="Loading..." />
            ) : dashboardError ? (
              <span className="text-white/80 text-base">Error</span>
            ) : (
              formatCurrencyWithFallback(dashboardStats?.pendingMyApprovalValue)
            )
          }
        />
      </div>

      {/* Job Cards Overview (Mock UI) */}
      <div className="mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
        <h2 className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-4">Job Cards Overview</h2>
        {(() => {
          const userRoleId = (user as any)?.user_role_id;
          const isRegionalCoach = userRoleId === 3;
          const jobCardSummary = jcSummary ?? { total: 0, open: 0, closed: 0 };
          const regionalCoaches: Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }> = jcRegionalCoaches;
          const areaCoaches: Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }> = jcAreaCoaches;
          const filteredStoreRows = jcStores;
          const sortedStoreRows = [...filteredStoreRows].sort((a, b) => {
            const totalA = a.open + a.closed;
            const totalB = b.open + b.closed;
            const pctA = getCompletionPct(a.closed, totalA);
            const pctB = getCompletionPct(b.closed, totalB);
            let cmp = 0;
            switch (storeSortKey) {
              case 'name':
                cmp = (a.name || '').localeCompare(b.name || '');
                break;
              case 'open':
                cmp = a.open - b.open;
                break;
              case 'closed':
                cmp = a.closed - b.closed;
                break;
              case 'total':
                cmp = totalA - totalB;
                break;
              case 'pct':
                cmp = pctA - pctB;
                break;
            }
            return storeSortDir === 'asc' ? cmp : -cmp;
          });

          const onSort = (key: 'name' | 'open' | 'closed' | 'total' | 'pct') => {
            if (storeSortKey === key) {
              setStoreSortDir(storeSortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setStoreSortKey(key);
              setStoreSortDir('asc');
            }
          };
          const sortArrow = (key: 'name' | 'open' | 'closed' | 'total' | 'pct') => storeSortKey === key ? (storeSortDir === 'asc' ? '▲' : '▼') : '';
          return (
            <div className="space-y-6">
              {jcError && (
                <div className="p-3 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                  {jcError}
                </div>
              )}
              {/* Row 1: Global Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <InfoCard
                  label="Total"
                  gradient="from-sky-500 to-blue-600"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v4H3zM3 9h18v12H3z" />
                    </svg>
                  }
                  value={
                    <div className="flex flex-col">
                      <span className="text-2xl sm:text-4xl font-bold">{jobCardSummary.total}</span>
                      <span className="text-white/80 text-xs">Completion {getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                    </div>
                  }
                />
                <InfoCard
                  label="Open"
                  gradient="from-amber-500 to-yellow-500"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                    </svg>
                  }
                  value={
                    <div className="flex flex-col">
                      <span className="text-2xl sm:text-4xl font-bold">{jobCardSummary.open}</span>
                      <span className="text-white/80 text-xs">Remaining {100 - getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                    </div>
                  }
                />
                <InfoCard
                  label="Closed"
                  gradient="from-emerald-600 to-teal-400"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  }
                  value={
                    <div className="flex flex-col">
                      <span className="text-2xl sm:text-4xl font-bold">{jobCardSummary.closed}</span>
                      <span className="text-white/80 text-xs">Completion {getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                    </div>
                  }
                />
              </div>

              {isAdmin && (
                <div>
                  <button
                    type="button"
                    onClick={() => setIsRegionalCoachesOpen(v => !v)}
                    className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg font-medium">Regional Coaches</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{regionalCoaches.length}</span>
                      {selectedRegionalCoach && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 truncate max-w-[8rem] sm:max-w-[12rem]">
                          {selectedRegionalCoach.name}
                        </span>
                      )}
                    </span>
                    <span className="text-xs opacity-80">{isRegionalCoachesOpen ? 'Hide' : 'Show'}</span>
                  </button>
                  <div className={`${isRegionalCoachesOpen ? 'block' : 'hidden'}`}>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click a Regional Coach card to filter Area Coaches and the stores table below.</p>
                    {(!jcLoading && jcError === null && regionalCoaches.length === 0) && (
                      <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3">
                        No Regional Coaches found.
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {regionalCoaches.map((rc) => (
                        <button
                          type="button"
                          key={rc.id}
                          onClick={() => {
                            if (selectedRegionalCoach?.id === rc.id) {
                              setSelectedRegionalCoach(null);
                            } else {
                              setSelectedRegionalCoach({ id: rc.id, name: rc.name });
                              // reset area coach selection when regional changes
                              setSelectedAreaCoach(null);
                              // ensure Area Coaches section stays open for selection after regional filter
                              setIsAreaCoachesOpen(true);
                              setIsRegionalCoachesOpen(false);
                            }
                          }}
                          className={`text-left shadow-lg rounded-xl p-5 bg-white dark:bg-gray-800 border ${selectedRegionalCoach?.id === rc.id ? 'border-blue-500' : 'border-gray-100 dark:border-gray-700'} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-base font-semibold text-gray-800 dark:text-gray-200">{rc.name}</div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                            </svg>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600">
                              <span className="opacity-90">Total</span>
                              <span className="text-white/90">{rc.summary.total}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500">
                              <span className="opacity-90">Open</span>
                              <span className="text-white/90">{rc.summary.open}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-400">
                              <span className="opacity-90">Closed</span>
                              <span className="text-white/90">{rc.summary.closed}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600">
                              <span className="opacity-90">Completion</span>
                              <span className="text-white/90">{getCompletionPct(rc.summary.closed, rc.summary.total)}%</span>
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Row 2: Area Coaches (regional only) */}
              {(isRegionalCoach || isAdmin) && (
                <div>
                  <button
                    type="button"
                    onClick={() => setIsAreaCoachesOpen(v => !v)}
                    className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg font-medium">Area Coaches</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{areaCoaches.length}</span>
                      {selectedAreaCoach && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 truncate max-w-[8rem] sm:max-w-[12rem]">
                          {selectedAreaCoach.name}
                        </span>
                      )}
                    </span>
                    <span className="text-xs opacity-80">{isAreaCoachesOpen ? 'Hide' : 'Show'}</span>
                  </button>
                  <div className={`${isAreaCoachesOpen ? 'block' : 'hidden'}`}>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click an Area Coach card to filter the stores table below.</p>
                    {(!jcLoading && jcError === null && areaCoaches.length === 0) && (
                      <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3">
                        No Area Coaches found.
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {areaCoaches.map((ac) => (
                        <button
                          type="button"
                          key={ac.id}
                          onClick={() => {
                            if (selectedAreaCoach?.id === ac.id) {
                              setSelectedAreaCoach(null);
                            } else {
                              setSelectedAreaCoach({ id: ac.id, name: ac.name });
                              setIsAreaCoachesOpen(false);
                            }
                          }}
                          className={`text-left shadow-lg rounded-xl p-5 bg-white dark:bg-gray-800 border ${selectedAreaCoach?.id === ac.id ? 'border-blue-500' : 'border-gray-100 dark:border-gray-700'} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-base font-semibold text-gray-800 dark:text-gray-200">
                              {ac.name}
                            </div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                            </svg>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600">
                              <span className="opacity-90">Total</span>
                              <span className="text-white/90">{ac.summary.total}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500">
                              <span className="opacity-90">Open</span>
                              <span className="text-white/90">{ac.summary.open}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-400">
                              <span className="opacity-90">Closed</span>
                              <span className="text-white/90">{ac.summary.closed}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600">
                              <span className="opacity-90">Completion</span>
                              <span className="text-white/90">{getCompletionPct(ac.summary.closed, ac.summary.total)}%</span>
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Selected AC pill and clear filter */}
              {(selectedAreaCoach || selectedRegionalCoach) && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="inline-flex flex-wrap items-center gap-2 text-xs sm:text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 max-w-full">
                    <span>Filtering by:</span>
                    {selectedRegionalCoach && (
                      <>
                        <span className="opacity-80">RC</span>
                        <strong className="truncate max-w-[10rem] sm:max-w-none">{selectedRegionalCoach.name}</strong>
                      </>
                    )}
                    {selectedAreaCoach && (
                      <>
                        <span className="opacity-60">•</span>
                        <span className="opacity-80">AC</span>
                        <strong className="truncate max-w-[10rem] sm:max-w-none">{selectedAreaCoach.name}</strong>
                      </>
                    )}
                  </div>
                  <button
                    className="text-xs sm:text-sm px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 w-full sm:w-auto"
                    onClick={() => { setSelectedAreaCoach(null); setSelectedRegionalCoach(null); }}
                  >
                    Clear Filter
                  </button>
                </div>
              )}

              {/* Table: Per-store breakdown */}
              <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200">Store Job Cards</div>
                  <button
                    className="text-sm px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 w-full sm:w-auto"
                    onClick={() => exportStoresToCsv(sortedStoreRows)}
                  >
                    Export CSV
                  </button>
                </div>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('name')}>Store <span className="ml-1">{sortArrow('name')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('open')}>Open <span className="ml-1">{sortArrow('open')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('closed')}>Closed <span className="ml-1">{sortArrow('closed')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('total')}>Total <span className="ml-1">{sortArrow('total')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('pct')}>Completion % <span className="ml-1">{sortArrow('pct')}</span></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedStoreRows.map((row: { id: number; name: string; storenumber?: string; open: number; closed: number; total: number }) => {
                      const total = row.open + row.closed;
                      const pct = getCompletionPct(row.closed, total);
                      return (
                        <tr key={row.id}>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{row.name}{row.storenumber ? ` - ${row.storenumber}` : ''}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-amber-600 dark:text-amber-400">{row.open}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-emerald-600 dark:text-emerald-400">{row.closed}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">{total}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {jcLoading && (
                <div className="pt-3"><LoadingSpinner size="md" variant="dark" label="Loading job cards..." /></div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default DashboardPage;
